import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useParticipationsClient } from '@/api/participations-client-context';
import type {
  ParticipationsClient,
  SupplierOpenCampaignFlagDto,
  SupplierParticipationsBySiretDto,
  SupplierParticipationSummaryDto,
} from '@/api/participations-client';

// =============================================================================
// use-participations.ts — Hook TanStack Query du résumé participations (T2-REGISTRE).
// =============================================================================
// Lecture composée : le registre (page courante) fournit les SIRET ; ce hook résout, en UN batch-read,
// le compteur + la dernière date de référencement PAR SIRET (colonnes « Participations » /
// « Dernière date de référencement »). Appelé avec les SIRET de la PAGE courante uniquement
// (≤ pageSize ≤ 100 ≪ 500) : le volume reste borné, sans chunking en pratique.
//
// ZÉRO règle métier ici : l'autorité (comptage, MAX(submittedAt), RBAC AdminSessionGuard) est l'API.

/** Clé de base des résumés de participations. */
const PARTICIPATIONS_KEY = ['participations', 'summary'] as const;

/** Clé de base du flag « campagne ouverte » par SIRET (avertissement transfert MASTER, T4). */
const OPEN_CAMPAIGN_KEY = ['participations', 'open-campaign'] as const;

/** Clé de base de la chronologie des participations par SIRET (fiche T8). DISTINCTE de open-campaign. */
const PARTICIPATIONS_BY_SIRET_KEY = ['participations', 'by-siret'] as const;

/**
 * Résumé des participations pour un ensemble de SIRET. `enabled` ssi la liste est non vide (aucun
 * appel réseau autrement). La `queryKey` porte le set TRIÉ (indépendante de l'ordre des SIRET de la
 * page → cache stable). `keepPreviousData` évite le flash entre deux pages.
 */
export function useParticipationsSummary(
  sirets: readonly string[],
): UseQueryResult<readonly SupplierParticipationSummaryDto[], Error> {
  const client: ParticipationsClient = useParticipationsClient();
  const sortedSirets = [...sirets].sort();
  return useQuery<readonly SupplierParticipationSummaryDto[], Error>({
    queryKey: [...PARTICIPATIONS_KEY, sortedSirets],
    queryFn: () => client.summarizeParticipations(sirets),
    enabled: sirets.length > 0,
    placeholderData: keepPreviousData,
  });
}

/**
 * Flag « campagne ouverte » d'un SIRET (avertissement transfert MASTER, DBU §2.2 l.712). Fetch PILOTÉ
 * par `enabled` : câblé à L'OUVERTURE du dialog de transfert (jamais au montage de la fiche → évite un
 * appel réseau par fiche). ZÉRO règle métier : l'autorité (périmètre « ouverte » PROTECTEUR,
 * AdminSessionGuard) est l'API ; l'UI ne fait qu'AVERTIR (CAUTION, jamais blocage).
 */
export function useSupplierOpenCampaignFlag(
  siret: string,
  options: { readonly enabled: boolean },
): UseQueryResult<SupplierOpenCampaignFlagDto, Error> {
  const client: ParticipationsClient = useParticipationsClient();
  return useQuery<SupplierOpenCampaignFlagDto, Error>({
    queryKey: [...OPEN_CAMPAIGN_KEY, siret],
    queryFn: () => client.getOpenCampaignFlag(siret),
    enabled: options.enabled,
  });
}

/**
 * Chronologie des participations d'un SIRET (fiche T8, DBU §2.2 l.694). Montée AVEC la fiche
 * (contrairement au flag T4 piloté par `enabled`) : la chronologie est une section TOUJOURS visible de
 * la fiche → fetch au montage. ZÉRO règle métier : l'autorité (tri « plus récente d'abord », RBAC
 * AdminSessionGuard) est l'API. Endpoint DISTINCT de T4 (queryKey `by-siret` ≠ `open-campaign`).
 */
export function useSupplierParticipationsBySiret(
  siret: string,
): UseQueryResult<SupplierParticipationsBySiretDto, Error> {
  const client: ParticipationsClient = useParticipationsClient();
  return useQuery<SupplierParticipationsBySiretDto, Error>({
    queryKey: [...PARTICIPATIONS_BY_SIRET_KEY, siret],
    queryFn: () => client.getParticipationsBySiret(siret),
    enabled: siret.length > 0,
  });
}
